import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronDown, Search } from "lucide-react"
import en from "../i18n/en"

// Checkbox multi-select for FilterBar's `type: "multiselect"` fields (e.g.
// the OR/PO/PR `item` filter, which the BE accepts as several IDs). Kept as
// a plain button + popover with real <input type="checkbox"> rows — no new
// dependency (react-select/MUI) pulled into the design system for this.
//
// Controlled: `value` is an array of string ids, `onChange` receives the
// next array. FilterBar itself stores this as a CSV string so it round-trips
// through the URL query string the same way every other filter does.
export const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder,
  searchPlaceholder,
  disabled,
  loading,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDocMouseDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const selectedSet = useMemo(() => new Set((value || []).map(String)), [value])

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])

  const toggle = (optValue) => {
    const v = String(optValue)
    const next = selectedSet.has(v)
      ? (value || []).filter((x) => String(x) !== v)
      : [...(value || []), v]
    onChange?.(next)
  }

  const summary = () => {
    if (!value?.length) return placeholder || en.common.all
    if (value.length === 1) {
      const opt = options.find((o) => String(o.value) === String(value[0]))
      return opt ? opt.label : value[0]
    }
    return en.common.selected(value.length)
  }

  return (
    <div className="ds-multiselect" ref={rootRef}>
      <button
        type="button"
        className="pc-control ds-multiselect-trigger"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ds-multiselect-trigger-label">{summary()}</span>
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <div className="ds-multiselect-panel" role="listbox" aria-multiselectable="true">
          <span className="ds-multiselect-search">
            <Search size={13} aria-hidden />
            <input
              type="search"
              autoFocus
              placeholder={searchPlaceholder || en.filter.searchItem}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </span>
          <div className="ds-multiselect-list">
            {loading ? (
              <div className="ds-multiselect-empty">{en.common.loading}</div>
            ) : filteredOptions.length === 0 ? (
              <div className="ds-multiselect-empty">{en.filter.noMatches}</div>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.value} className="ds-multiselect-option">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(String(o.value))}
                    onChange={() => toggle(o.value)}
                  />
                  <span>{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiSelect
